import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText } from '../../../../../styling/stylingHelper';
import type { FlaggedProductWithRestaurant } from '../../../../../services/admin/adminService';
import { PlatformCopyCell, PromptCopyCell } from '../shared/copyCells';
import { FlaggedProductDetailButton } from './FlaggedProductDetailButton';
import type { TFn } from '../types';

/** Stable identity for a flagged product, so dismissals survive re-grouping. */
export function flaggedProductKey(p: FlaggedProductWithRestaurant): string {
  return `${p.restaurantId}|${p.platform}|${p.articleId}|${p.productName}|${p.gmailId ?? ''}`;
}

/** Shared flagged-product table — used by both the reason view and the AI issue-type view. */
export function FlaggedProductTable({
  products,
  t,
  dismissed,
  onDismiss,
}: {
  products: FlaggedProductWithRestaurant[];
  t: TFn;
  dismissed: Set<string>;
  onDismiss: (key: string) => void;
}) {
  const visible = products.filter((p) => !dismissed.has(flaggedProductKey(p)));
  if (visible.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className={cn(secondaryText, 'border-b border-gray-200 dark:border-gray-600')}>
            <th className="py-1 px-2 w-6"></th>
            <th className="text-left py-1 px-2">{t('adminTesting.qtyAuditAll.col.restaurant')}</th>
            <th className="text-left py-1 px-2">{t('adminTesting.qtyAuditAll.col.articleId')}</th>
            <th className="text-left py-1 px-2">{t('adminTesting.qtyAuditAll.col.product')}</th>
            <th className="text-right py-1 px-2">{t('adminTesting.qtyAuditAll.col.qty')}</th>
            <th className="text-left py-1 px-2">{t('adminTesting.qtyAuditAll.col.quantityInfo')}</th>
            <th className="text-right py-1 px-2">{t('adminTesting.qtyAuditAll.col.unitPrice')}</th>
            <th className="text-left py-1 px-2">{t('adminTesting.qtyAuditAll.col.gmail')}</th>
            <th className="py-1 px-2 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {visible.map((p, pi) => (
            <tr key={flaggedProductKey(p)} className={cn('border-b border-gray-100 dark:border-gray-700', pi % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : '')}>
              <td className="py-1 px-2 text-center"><PromptCopyCell product={p} /></td>
              <td className={cn(primaryText, 'py-1 px-2 max-w-[160px] truncate')} title={p.restaurantName}>{p.restaurantName}</td>
              <td className={cn(primaryText, 'py-1 px-2 font-mono')}>{p.articleId || '-'}</td>
              <td className={cn(primaryText, 'py-1 px-2 max-w-xs truncate')} title={p.productName}>{p.productName}</td>
              <td className={cn(primaryText, 'py-1 px-2 font-mono text-right')}>{p.quantity % 1 === 0 ? p.quantity : p.quantity.toFixed(3)}</td>
              <td className={cn(secondaryText, 'py-1 px-2 font-mono whitespace-nowrap')}>
                {p.quantityInfo ? (
                  <span>{p.quantityInfo.stdMultiplier} x {p.quantityInfo.stdAmount}{p.quantityInfo.stdUnit}</span>
                ) : (
                  <span className="text-red-500 dark:text-textRed-dark">null</span>
                )}
              </td>
              <td className={cn(primaryText, 'py-1 px-2 font-mono text-right')}>€{p.unitPrice.toFixed(2)}</td>
              <td className={cn(primaryText, 'py-1 px-2 font-mono')}><PlatformCopyCell platform={p.platform} gmailId={p.gmailId} /></td>
              <td className="py-1 px-2">
                <div className="flex items-center justify-center gap-1.5">
                  <FlaggedProductDetailButton product={p} t={t} />
                  <button
                    onClick={() => onDismiss(flaggedProductKey(p))}
                    className="text-gray-400 dark:text-textQuaternary-dark hover:text-red-500 dark:hover:text-textRed-dark transition-colors"
                    title={t('adminTesting.flaggedDetail.dismiss')}
                  >
                    ✕
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
